import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Plus, Send, Trash2, Search } from "lucide-react";
import { fetchWithAuth } from "@/lib/auth-client";
import type {
  PurchaseRequestListItemDto,
  PurchaseRequestDetailDto,
  PRLineItemDto,
  VendorDto,
  CreateRFQFromPRsRequest,
  UpdateRFQRequest,
  RFQDto,
} from "@procureflow/shared-types";
import { remainingQty, validateBuildRows, type BuildRow as BuildRowModel } from "@/lib/rfq-build";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// US-063 (m2m-document-linkage) — Create-RFQ-from-multiple-PRs builder.
//
// A standalone form reached from a "Create RFQ" button on the RFQ list. Each
// row has TWO cascading lookups: (1) pick an open source PR (Approved /
// PartiallyConsumed with remaining qty), (2) pick a line of that PR (only lines
// with remaining > 0). The form prevents over-consuming (qty <= remaining),
// then POSTs /api/rfqs/from-prs and navigates to the new RFQ detail.
//
// The legacy single-PR path (rfq_.new.tsx, /rfq/new?prId=) is left intact.

// US-077 — the same builder doubles as the draft-edit form when reached with
// ?editId=<rfqId> (from the Edit affordance on an Open RFQ's detail). In edit mode it
// pre-loads the RFQ's current lines/vendors/deadline and PUTs the changes (release +
// re-consume on the source PR happens server-side); otherwise it POSTs a new RFQ.
export const Route = createFileRoute("/rfq_/build")({
  validateSearch: (search: Record<string, unknown>): { editId?: string } => ({
    editId: typeof search.editId === "string" ? search.editId : undefined,
  }),
  head: () => ({
    meta: [{ title: "Build RFQ — ProcureFlow" }],
  }),
  component: BuildRFQPage,
});

interface RowState {
  key: string;
  sourcePrId: string;
  sourcePrNumber: string;
  sourcePrLineItemId: string;
  lineDescription: string;
  lineUom: string;
  lineRemaining: number;
  quantity: number;
}

function newRow(): RowState {
  return {
    key: crypto.randomUUID(),
    sourcePrId: "",
    sourcePrNumber: "",
    sourcePrLineItemId: "",
    lineDescription: "",
    lineUom: "",
    lineRemaining: 0,
    quantity: 1,
  };
}

function BuildRFQPage() {
  const navigate = useNavigate();
  const { editId } = Route.useSearch();
  const isEdit = Boolean(editId);

  const [prs, setPrs] = useState<PurchaseRequestListItemDto[]>([]);
  const [vendors, setVendors] = useState<VendorDto[]>([]);
  const [selectedVendorIds, setSelectedVendorIds] = useState<string[]>([]);
  const [deadline, setDeadline] = useState("");
  const [rows, setRows] = useState<RowState[]>([newRow()]);
  // Cache of PR detail (lines) by PR id, lazily fetched when a PR is picked.
  const [prDetails, setPrDetails] = useState<Record<string, PurchaseRequestDetailDto>>({});
  const [loading, setLoading] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // fetchWithAuth awaits mswReady internally (auth-client) so the MSW
        // mount-time fetch pattern is satisfied without an explicit await here.
        const [prRes, vRes] = await Promise.all([
          fetchWithAuth("/api/purchase-requests?status=Approved,PartiallyConsumed&limit=100"),
          fetchWithAuth("/api/vendors?status=Active"),
        ]);
        const prJson = prRes.ok
          ? ((await prRes.json()) as { data: PurchaseRequestListItemDto[] })
          : { data: [] };
        const vJson = vRes.ok ? ((await vRes.json()) as { data: VendorDto[] }) : { data: [] };
        if (cancelled) return;
        setPrs(prJson.data ?? []);
        setVendors((vJson.data ?? []).filter((v) => v.status === "Active"));

        // US-077 edit mode — pre-load the existing RFQ's lines/vendors/deadline. The
        // PR line's "remaining" in the UI is bumped by this RFQ's existing consumption
        // so the user can keep or raise their own quantities (the server re-checks the
        // effective remaining = current remaining + this RFQ's release).
        if (editId) {
          const rfqRes = await fetchWithAuth(`/api/rfqs/${editId}`);
          if (rfqRes.ok) {
            const { data: rfq } = (await rfqRes.json()) as { data: RFQDto };
            if (cancelled) return;
            setDeadline(rfq.submission_deadline.slice(0, 16));
            setSelectedVendorIds(rfq.vendors.map((v) => v.vendor_id));
            // Fetch each distinct source PR's detail to resolve line remaining.
            const sourcePrIds = [
              ...new Set(
                rfq.line_items.map((li) => li.source_pr_id).filter((x): x is string => !!x),
              ),
            ];
            const detailEntries = await Promise.all(
              sourcePrIds.map(async (pid) => {
                const r = await fetchWithAuth(`/api/purchase-requests/${pid}`);
                return r.ok
                  ? ([pid, ((await r.json()) as { data: PurchaseRequestDetailDto }).data] as const)
                  : null;
              }),
            );
            const detailMap: Record<string, PurchaseRequestDetailDto> = {};
            for (const e of detailEntries) if (e) detailMap[e[0]] = e[1];
            if (cancelled) return;
            setPrDetails(detailMap);
            const editRows: RowState[] = rfq.line_items.map((li) => {
              const prDetail = li.source_pr_id ? detailMap[li.source_pr_id] : undefined;
              const prLine = prDetail?.line_items.find((p) => p.id === li.source_pr_line_item_id);
              const currentRemaining = prLine ? remainingQty(prLine) : 0;
              return {
                key: crypto.randomUUID(),
                sourcePrId: li.source_pr_id ?? "",
                sourcePrNumber: li.source_pr_number ?? "",
                sourcePrLineItemId: li.source_pr_line_item_id ?? "",
                lineDescription: li.description,
                lineUom: li.unit_of_measure,
                // Add back this RFQ line's own consumption so editing its qty isn't blocked.
                lineRemaining: currentRemaining + li.quantity,
                quantity: li.quantity,
              };
            });
            if (editRows.length > 0) setRows(editRows);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [editId]);

  // Lines of a PR with remaining > 0, from the cached detail.
  function availableLines(prId: string): PRLineItemDto[] {
    const detail = prDetails[prId];
    if (!detail) return [];
    return detail.line_items.filter((li) => remainingQty(li) > 0);
  }

  async function pickPr(rowKey: string, pr: PurchaseRequestListItemDto) {
    // Lazily fetch the PR's detail (line items + remaining) once.
    if (!prDetails[pr.id]) {
      const res = await fetchWithAuth(`/api/purchase-requests/${pr.id}`);
      if (res.ok) {
        const { data } = (await res.json()) as { data: PurchaseRequestDetailDto };
        setPrDetails((prev) => ({ ...prev, [pr.id]: data }));
      }
    }
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey
          ? {
              ...r,
              sourcePrId: pr.id,
              sourcePrNumber: pr.pr_number,
              // Reset the line selection when the PR changes.
              sourcePrLineItemId: "",
              lineDescription: "",
              lineUom: "",
              lineRemaining: 0,
            }
          : r,
      ),
    );
  }

  function pickLine(rowKey: string, line: PRLineItemDto) {
    const remaining = remainingQty(line);
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey
          ? {
              ...r,
              sourcePrLineItemId: line.id,
              lineDescription: line.description,
              lineUom: line.unit_of_measure,
              lineRemaining: remaining,
              quantity: Math.min(r.quantity || 1, remaining) || 1,
            }
          : r,
      ),
    );
  }

  function updateQty(rowKey: string, qty: number) {
    setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, quantity: qty } : r)));
  }

  function addRow() {
    setRows((prev) => [...prev, newRow()]);
  }

  function removeRow(rowKey: string) {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== rowKey) : prev));
  }

  function toggleVendor(id: string, checked: boolean) {
    setSelectedVendorIds((prev) => (checked ? [...prev, id] : prev.filter((v) => v !== id)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!deadline || new Date(deadline).getTime() <= Date.now()) {
      setFormError("Submission deadline must be in the future.");
      return;
    }

    const modelRows: BuildRowModel[] = rows
      .filter((r) => r.sourcePrLineItemId)
      .map((r) => ({
        sourcePrId: r.sourcePrId,
        sourcePrLineItemId: r.sourcePrLineItemId,
        quantity: r.quantity,
      }));
    const remainingById = new Map<string, number>();
    rows.forEach((r) => {
      if (r.sourcePrLineItemId) remainingById.set(r.sourcePrLineItemId, r.lineRemaining);
    });
    const validationError = validateBuildRows(modelRows, remainingById);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const lines = modelRows.map((r) => ({
        source_pr_line_item_id: r.sourcePrLineItemId,
        quantity: r.quantity,
      }));
      if (isEdit && editId) {
        const body: UpdateRFQRequest = {
          submission_deadline: new Date(deadline).toISOString(),
          vendor_ids: selectedVendorIds.length > 0 ? selectedVendorIds : undefined,
          lines,
        };
        const res = await fetchWithAuth(`/api/rfqs/${editId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const errJson = await res.json().catch(() => ({}) as { message?: string });
          setFormError(errJson?.message ?? "Failed to save changes.");
          return;
        }
        navigate({ to: "/rfq/$id", params: { id: editId } });
        return;
      }
      const body: CreateRFQFromPRsRequest = {
        submission_deadline: new Date(deadline).toISOString(),
        vendor_ids: selectedVendorIds.length > 0 ? selectedVendorIds : undefined,
        lines,
      };
      const res = await fetchWithAuth("/api/rfqs/from-prs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.status !== 201) {
        const errJson = await res.json().catch(() => ({}) as { message?: string });
        setFormError(errJson?.message ?? "Failed to create RFQ.");
        return;
      }
      const okJson = (await res.json()) as { data: RFQDto };
      navigate({ to: "/rfq/$id", params: { id: okJson.data.id } });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Link
        to="/rfq"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> RFQs
      </Link>

      <div className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-2">
        <h1 className="text-lg font-semibold tracking-tight">
          {isEdit ? "Edit draft RFQ" : "Build RFQ from purchase requests"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isEdit
            ? "Adjust the header and line items. Quantity changes re-sync the source purchase request's consumption."
            : "Draw line items from one or more approved purchase requests into a single RFQ. Each line traces back to the PR line it came from."}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Line builder — two cascading lookups per row. */}
        <section className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">Lines</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Pick a source PR, then a line of that PR. Quantity cannot exceed the remaining
                orderable amount.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRow}
              data-testid="add-line-button"
            >
              <Plus className="h-3.5 w-3.5" /> Add line
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/40">
                <tr>
                  <th className="px-4 py-2.5 text-left w-56">Source PR</th>
                  <th className="px-4 py-2.5 text-left">PR line</th>
                  <th className="px-3 py-2.5 text-left w-28">Remaining</th>
                  <th className="px-3 py-2.5 text-left w-28">Qty</th>
                  <th className="px-3 py-2.5 w-12" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.key} className="border-t border-border align-top">
                    {/* Lookup 1 — source PR */}
                    <td className="px-4 py-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Line ${idx + 1} source PR`}
                            className="inline-flex w-full items-center justify-between gap-2 rounded border border-border bg-background px-2.5 py-2 text-left text-xs hover:bg-accent transition"
                          >
                            <span
                              className={row.sourcePrNumber ? "font-mono" : "text-muted-foreground"}
                            >
                              {row.sourcePrNumber || "Select a PR…"}
                            </span>
                            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-80 p-0 overflow-hidden">
                          <div className="max-h-60 overflow-y-auto">
                            {prs.length === 0 ? (
                              <p className="px-3 py-3 text-xs text-muted-foreground">
                                No open purchase requests available.
                              </p>
                            ) : (
                              <ul>
                                {prs.map((pr) => (
                                  <li key={pr.id}>
                                    <button
                                      type="button"
                                      data-testid={`pr-option-${pr.id}`}
                                      onClick={() => pickPr(row.key, pr)}
                                      className="grid w-full grid-cols-[8rem_minmax(0,1fr)] items-center gap-2 border-t border-border/60 px-3 py-2 text-left text-xs transition hover:bg-accent first:border-t-0"
                                    >
                                      <span className="truncate font-mono font-medium">
                                        {pr.pr_number}
                                      </span>
                                      <span className="truncate text-muted-foreground">
                                        {pr.title}
                                      </span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </td>

                    {/* Lookup 2 — PR line (only lines with remaining > 0) */}
                    <td className="px-4 py-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Line ${idx + 1} PR line`}
                            disabled={!row.sourcePrId}
                            className="inline-flex w-full items-center justify-between gap-2 rounded border border-border bg-background px-2.5 py-2 text-left text-xs hover:bg-accent transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className={row.lineDescription ? "" : "text-muted-foreground"}>
                              {row.lineDescription || "Select a line…"}
                            </span>
                            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-96 p-0 overflow-hidden">
                          <div className="max-h-60 overflow-y-auto">
                            {availableLines(row.sourcePrId).length === 0 ? (
                              <p className="px-3 py-3 text-xs text-muted-foreground">
                                No lines with remaining quantity on this PR.
                              </p>
                            ) : (
                              <ul>
                                {availableLines(row.sourcePrId).map((line) => (
                                  <li key={line.id}>
                                    <button
                                      type="button"
                                      data-testid={`pr-line-option-${line.id}`}
                                      onClick={() => pickLine(row.key, line)}
                                      className="grid w-full grid-cols-[minmax(0,1fr)_4rem] items-center gap-2 border-t border-border/60 px-3 py-2 text-left text-xs transition hover:bg-accent first:border-t-0"
                                    >
                                      <span className="truncate">{line.description}</span>
                                      <span className="text-right tabular-nums text-muted-foreground">
                                        {remainingQty(line)} {line.unit_of_measure}
                                      </span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </td>

                    <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">
                      {row.sourcePrLineItemId ? `${row.lineRemaining} ${row.lineUom}` : "—"}
                    </td>

                    <td className="px-3 py-2">
                      <Input
                        aria-label={`Line ${idx + 1} quantity`}
                        type="number"
                        min={1}
                        max={row.lineRemaining || undefined}
                        value={row.quantity}
                        disabled={!row.sourcePrLineItemId}
                        onChange={(e) => updateQty(row.key, parseInt(e.target.value, 10) || 0)}
                      />
                    </td>

                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        aria-label={`Remove line ${idx + 1}`}
                        onClick={() => removeRow(row.key)}
                        disabled={rows.length === 1}
                        className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive transition disabled:opacity-30"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Vendor multi-select (optional at build time). */}
        <section className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-semibold">Vendors</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Optionally select Active vendors to invite now. You can also distribute later.
            </p>
          </div>
          <div role="group" aria-label="Vendor selection" className="px-5 py-4 space-y-2">
            {vendors.length === 0 ? (
              <p className="text-sm text-muted-foreground">No Active vendors available.</p>
            ) : (
              vendors.map((v) => (
                <label key={v.id} className="flex items-center gap-2.5 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    aria-label={`Select ${v.company_name}`}
                    checked={selectedVendorIds.includes(v.id)}
                    onChange={(e) => toggleVendor(v.id, e.target.checked)}
                    className="h-4 w-4"
                  />
                  <span>{v.company_name}</span>
                </label>
              ))
            )}
          </div>
        </section>

        {/* Submission deadline. */}
        <section className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-semibold">Submission deadline</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Vendors must submit quotes before this date and time.
            </p>
          </div>
          <div className="px-5 py-4 space-y-2 max-w-md">
            <Label htmlFor="submission-deadline">Submission deadline</Label>
            <Input
              id="submission-deadline"
              type="datetime-local"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </div>
        </section>

        {formError && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          >
            {formError}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="outline" type="button">
            <Link to="/rfq">Cancel</Link>
          </Button>
          <Button type="submit" disabled={submitting} data-testid="submit-build-button">
            <Send className="h-3.5 w-3.5" />
            {submitting
              ? isEdit
                ? "Saving…"
                : "Creating…"
              : isEdit
                ? "Save changes"
                : "Create RFQ"}
          </Button>
        </div>
      </form>
    </div>
  );
}
